import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  CalculationInput,
  BasicCalculationResult,
  FinalCalculationResult,
  ExplanationData,
  CalculatorData,
  ApiResponse,
  GreenRatingGroup,
  saveCalculationWithNotes,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import { formatThousands, toRawNumber } from "../../../utils/numberInput";
import GreenRatingCalculatorModal from "../../shared/GreenRatingCalculatorModal";
import CalculatorIcon from "../../shared/CalculatorIcon";
import ActualCostCalculator, {
  ActualCostData,
} from "../../shared/ActualCostCalculator";

interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
  vehicle_weight_over_3500: boolean;
  green_rating_group: string;
  road_entry_date: string;
  shipping_time_months: string;
}
interface SafetyEquipmentData {
  airbagsCount: number;
  systems: {
    blindSpot: boolean; // מערכת זיהוי כלי רכב ב"שטח מת" - 1.0
    sideCollisionActive: boolean; // מערכת אקטיבית למניעת התנגשות צד ב"שטח מת" - 2.0
    reverseBraking: boolean; // בלימה אוטומטית בנסיעה לאחור - 1.0
    fatigueWarning: boolean; // מערכת התרעה במצב של היסח הדעת הנהג - 1.0
    childAlert: boolean; // מערכת להתרעה על השארת ילדים ברכב - 1.5
    doorOpening: boolean; // מערכת אחזרת פתיחת דלת - 1.5
    adaptiveLighting: boolean; // מערכת תאורה אדפטיבית/שליטה באורות גבוהים - 1.0
    junctionBraking: boolean; // בלימת חירום להתנעה חוצה בצמתים - 1.5
  };
}
// ממשק מתוקן לתוצאות החישוב מה-API
interface CalculationResult {
  vehicle_info: {
    manufacturing_date: string;
    vehicle_price_foreign: number;
    currency_type: string;
    exchange_rate: number;
    is_heavy_vehicle: boolean;
    green_rating_info?: any;
  };
  base_calculations: {
    tax_value_ils: number;
    customs_fee: number;
    customs_rate_percent: number;
    purchase_tax_base: number;
    has_origin_declaration: boolean;
  };
  chosen_result: {
    title: string;
    consumer_price_used: number;
    total_cost: number;
    luxury_tax_applicable: boolean;
    luxury_tax_rate?: number;
    final_purchase_tax_rate: number;
    final_purchase_tax: number;
    effective_purchase_tax_rate?: number;
    vat_amount: number;
    months_on_road?: number;
    depreciation_rate?: number;
    p1_calculated?: number;
    p0_calculated?: number;
    shipping_time_months?: number;
  };
  alternative_result?: {
    title: string;
    consumer_price_used: number;
    total_cost: number;
    luxury_tax_applicable: boolean;
    luxury_tax_rate?: number;
    final_purchase_tax_rate: number;
    final_purchase_tax: number;
    effective_purchase_tax_rate?: number;
    vat_amount: number;
    months_on_road?: number;
    depreciation_rate?: number;
    p1_calculated?: number;
  };
  comparison_info?: {
    price_difference: number;
    total_cost_difference: number;
  };
}

const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
  vehicle_weight_over_3500: "weight_over_3500_personal",
  green_rating_group: "green_rating",
  road_entry_date: "road_entry_date_personal",
  shipping_time_months: "shipping_time_personal",
  target_consumer_price: "consumer_price_personal",
} as const;

const SAFETY_SYSTEMS_SCORES = {
  blindSpot: 1.0,
  sideCollisionActive: 2.0,
  reverseBraking: 1.0,
  fatigueWarning: 1.0,
  childAlert: 1.5,
  doorOpening: 1.5,
  adaptiveLighting: 1.0,
  junctionBraking: 1.5,
} as const;

const SAFETY_LEVELS = [
  { level: 0, minScore: 0, minAirbags: 0, discount: 0 },
  { level: 1, minScore: 1, minAirbags: 6, discount: 250 },
  { level: 2, minScore: 2, minAirbags: 6, discount: 600 },
  { level: 3, minScore: 3, minAirbags: 6, discount: 1000 },
  { level: 4, minScore: 4, minAirbags: 6, discount: 1600 },
  { level: 5, minScore: 5, minAirbags: 6, discount: 1850 },
  { level: 6, minScore: 6, minAirbags: 6, discount: 2050 },
  { level: 7, minScore: 8, minAirbags: 6, discount: 2250 },
  { level: 8, minScore: 10, minAirbags: 6, discount: 2400 },
] as const;

const M1VehicleCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State לשמירה נפרדת
  const [userNotes, setUserNotes] = useState<string>("");
  const [actualCostData, setActualCostData] = useState<ActualCostData | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // State לתוצאות
  const [greenRatingGroups, setGreenRatingGroups] = useState<
    GreenRatingGroup[]
  >([]);
  const [basicResult, setBasicResult] = useState<BasicCalculationResult | null>(
    null,
  );
  const [originalCalculationResult, setOriginalCalculationResult] =
    useState<CalculationResult | null>(null);
  const [customCalculationResult, setCustomCalculationResult] =
    useState<CalculationResult | null>(null);
  const [showGreenRatingModal, setShowGreenRatingModal] = useState(false);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // State למחיר יעד
  const [suggestedPrice, setSuggestedPrice] = useState<number>(0);
  const [userChosenPrice, setUserChosenPrice] = useState<string>("");
  const [isUsingCustomPrice, setIsUsingCustomPrice] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "EUR",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "EUR",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
    vehicle_weight_over_3500: false,
    green_rating_group: "17",
    road_entry_date: "",
    shipping_time_months: "0",
  });
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [safetyEquipmentData, setSafetyEquipmentData] =
    useState<SafetyEquipmentData>({
      airbagsCount: 0,
      systems: {
        blindSpot: false,
        sideCollisionActive: false,
        reverseBraking: false,
        fatigueWarning: false,
        childAlert: false,
        doorOpening: false,
        adaptiveLighting: false,
        junctionBraking: false,
      },
    });

  useEffect(() => {
    loadCalculatorData();
  }, []);

  useEffect(() => {
    if (formData.currency_type && currencyRates.length > 0) {
      const selectedCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.currency_type,
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          exchange_rate: Number(selectedCurrency.customs_rate).toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencyRates]);

  useEffect(() => {
    if (formData.insurance_currency_type && currencyRates.length > 0) {
      const selectedInsuranceCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.insurance_currency_type,
      );
      if (selectedInsuranceCurrency) {
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: Number(
            selectedInsuranceCurrency.customs_rate,
          ).toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencyRates]);

  const loadCalculatorData = async () => {
    setIsLoading(true);
    try {
      const response: ApiResponse<CalculatorData> =
        await calculatorService.getCalculatorData();

      // console.log("🔍 Debug - Calculator data response:", response); // הוסף שורה זו

      if (response.res === "OK" && response.data) {
        setCurrencyRates(response.data.currency_rates);
        setExplanations(response.data.explanations);
        setGreenRatingGroups(response.data.green_rating_groups || []);

        // console.log(
        //   "🔍 Debug - Green rating groups:",
        //   response.data.green_rating_groups
        // ); // הוסף שורה זו
      } else {
        throw new Error(response.msg || "שגיאה בטעינת נתוני המחשבון");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "שגיאה בטעינת נתוני המחשבון",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const validateManufacturingDate = (
    dateStr: string,
    shippingMonths: string,
  ): string | null => {
    if (!dateStr) return "תאריך ייצור הוא שדה חובה";

    const manufacturingDate = new Date(dateStr);
    const currentDate = new Date();

    if (manufacturingDate > currentDate) {
      return "תאריך ייצור לא יכול להיות בעתיד";
    }

    // הוספת זמן הובלה לחישוב
    const shippingTimeMonths = parseInt(shippingMonths) || 0;
    const manufacturingPlusShipping = new Date(manufacturingDate);
    manufacturingPlusShipping.setMonth(
      manufacturingPlusShipping.getMonth() + shippingTimeMonths,
    );

    // תיקון: בדיקה אם תאריך הייצור + זמן הובלה עדיין בטווח של 24 חודשים מהיום
    const monthsDiff =
      (currentDate.getTime() - manufacturingDate.getTime()) /
      (1000 * 60 * 60 * 24 * 30.44);

    // התיקון העיקרי: הוספת זמן ההובלה לבדיקת 24 החודשים
    if (monthsDiff + shippingTimeMonths > 24) {
      return "לא ניתן לייבא רכב שתאריך הייצור שלו בתוספת זמן הובלה מעבר ל-24 חודשים";
    }

    return null;
  };

  const checkYearTransitionWarning = (
    manufacturingDateStr: string,
    shippingMonths: string,
  ): string | null => {
    if (!manufacturingDateStr || !shippingMonths) return null;

    const manufacturingDate = new Date(manufacturingDateStr);
    const shippingTimeMonths = parseInt(shippingMonths) || 0;

    if (shippingTimeMonths === 0) return null; // אם הרכב כבר בארץ

    const expectedArrivalDate = new Date(manufacturingDate);
    expectedArrivalDate.setMonth(
      expectedArrivalDate.getMonth() + shippingTimeMonths,
    );

    const currentYear = new Date().getFullYear();
    const arrivalYear = expectedArrivalDate.getFullYear();

    if (arrivalYear > currentYear) {
      return `⚠️ הרכב צפוי להגיע לישראל בשנה ${arrivalYear} (שנה אזרחית חדשה)`;
    }

    return null;
  };

  const validateRoadEntryDate = (
    roadDateStr: string,
    manufacturingDateStr: string,
  ): string | null => {
    if (!roadDateStr) return "תאריך עליה לכביש הוא שדה חובה";
    if (!manufacturingDateStr) return "נדרש תחילה תאריך ייצור";

    const roadDate = new Date(roadDateStr);
    const manufacturingDate = new Date(manufacturingDateStr);

    if (roadDate < manufacturingDate) {
      return "תאריך עליה לכביש לא יכול להיות לפני תאריך הייצור";
    }

    return null;
  };

  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    let cleanValue = value;
    if (value.length > 1 && value.startsWith("0") && !value.startsWith("0.")) {
      cleanValue = value.slice(1);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: cleanValue,
    }));
  };

  const validateStep1 = (): string[] => {
    const errors: string[] = [];

    const manufacturingError = validateManufacturingDate(
      formData.manufacturing_date,
      formData.shipping_time_months,
    );
    if (manufacturingError) errors.push(manufacturingError);

    if (!formData.vehicle_price_foreign) errors.push("ערך הרכב הוא שדה חובה");
    if (!formData.exchange_rate) errors.push("שער חליפין הוא שדה חובה");
    if (formData.shipping_cost_foreign === "")
      errors.push("ערך הובלה הוא שדה חובה");
    if (!formData.insurance_exchange_rate)
      errors.push("שער חליפין ביטוח הוא שדה חובה");
    if (formData.local_expenses_ils === "")
      errors.push("הוצאות מקומיות הוא שדה חובה");

    const roadEntryError = validateRoadEntryDate(
      formData.road_entry_date,
      formData.manufacturing_date,
    );
    if (roadEntryError) errors.push(roadEntryError);

    return errors;
  };

  const createCalculationInput = (targetPrice?: number): CalculationInput => {
    // ✅ שליחת תאריך עליה לכביש מלא (כולל יום!)
    // console.log(" DEBUG - נתוני חישוב:");
    // console.log("תאריך ייצור:", formData.manufacturing_date);
    // console.log("תאריך עליה לכביש:", formData.road_entry_date);
    // console.log(
    //   "זמן הובלה (חודשים):",
    //   parseInt(formData.shipping_time_months) || 0
    // );
    return {
      manufacturing_date: formData.manufacturing_date,
      vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign) || 0,
      currency_type: formData.currency_type,
      exchange_rate: parseFloat(formData.exchange_rate) || 0,
      shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
      insurance_cost_foreign: parseFloat(formData.insurance_cost_foreign) || 0,
      insurance_currency_type: formData.insurance_currency_type,
      insurance_exchange_rate:
        parseFloat(formData.insurance_exchange_rate) || 0,
      local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
      has_origin_declaration: formData.has_origin_declaration,
      // vehicle_weight_over_3500: formData.vehicle_weight_over_3500,
      vehicle_weight_over_3500: false, // TEMP: disabled by client request - always send false
      green_rating_group: parseInt(formData.green_rating_group) || 17,
      road_entry_date: formData.road_entry_date, // ✅ תאריך מלא!
      target_consumer_price: targetPrice || 0,
      shipping_time_months: parseInt(formData.shipping_time_months) || 0,
    };
  };
  const calculateBasic = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const input = createCalculationInput(0);
      const response: ApiResponse<BasicCalculationResult> =
        await calculatorService.getBasicCalculationM1(input);

      if (response.res === "OK" && response.data) {
        setBasicResult(response.data);

        // חישוב המחיר המוצע
        const luxuryTaxCalc =
          response.data.calculation_details?.luxury_tax_calculation;
        const recommendedPrice = luxuryTaxCalc?.recommended_consumer_price || 0;

        const fallbackPrice =
          response.data.calculation_details?.intermediate_calculations
            ?.tax_value_ils || 0;
        const finalRecommendedPrice =
          recommendedPrice > 0 ? recommendedPrice : fallbackPrice;

        setSuggestedPrice(finalRecommendedPrice);
        setUserChosenPrice(finalRecommendedPrice.toString());

        setCurrentStep(2);
      } else {
        throw new Error(response.msg || "שגיאה בחישוב בסיסי");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בחישוב בסיסי");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateFinal = async () => {
    if (!basicResult) return;

    const chosenPrice = parseFloat(userChosenPrice) || suggestedPrice;
    const usingCustomPrice = chosenPrice !== suggestedPrice;

    setIsLoading(true);
    setError(null);

    try {
      // חישוב עם המחיר שהמשתמש בחר
      const input = createCalculationInput(chosenPrice);
      const response: ApiResponse<FinalCalculationResult> =
        await calculatorService.getFinalCalculationM1(input, chosenPrice);

      if (response.res === "OK" && response.data) {
        const resultData = {
          vehicle_info: response.data.calculation_summary.vehicle_info,
          base_calculations:
            response.data.calculation_summary.base_calculations,
          chosen_result: response.data.calculation_summary.chosen_result,
          alternative_result:
            response.data.calculation_summary.alternative_result,
          comparison_info: response.data.calculation_summary.comparison_info,
        };

        if (usingCustomPrice) {
          setCustomCalculationResult(resultData);
          setIsUsingCustomPrice(true);

          // גם נחשב עם המחיר המקורי להשוואה
          const originalInput = createCalculationInput(suggestedPrice);
          const originalResponse =
            await calculatorService.getFinalCalculationM1(
              originalInput,
              suggestedPrice,
            );

          if (originalResponse.res === "OK" && originalResponse.data) {
            setOriginalCalculationResult({
              vehicle_info:
                originalResponse.data.calculation_summary.vehicle_info,
              base_calculations:
                originalResponse.data.calculation_summary.base_calculations,
              chosen_result:
                originalResponse.data.calculation_summary.chosen_result,
              alternative_result:
                originalResponse.data.calculation_summary.alternative_result,
              comparison_info:
                originalResponse.data.calculation_summary.comparison_info,
            });
          }
        } else {
          setOriginalCalculationResult(resultData);
          setIsUsingCustomPrice(false);
        }

        setCurrentStep(3);
      } else {
        throw new Error(response.msg || "שגיאה בחישוב סופי");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בחישוב סופי");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }
    if (currentStep === 1) {
      calculateBasic();
    } else if (currentStep === 2) {
      calculateFinal();
    }
  };

  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (currentStep === 3) {
        setOriginalCalculationResult(null);
        setCustomCalculationResult(null);
      }
    } else {
      navigate("/enduser/dashboard");
    }
  };

  const handleSaveCalculation = async () => {
    if (!originalCalculationResult && !customCalculationResult) return;

    setIsSaving(true);
    try {
      const resultToSave = isUsingCustomPrice
        ? customCalculationResult
        : originalCalculationResult;

      const saveRequest = {
        calculation_type: "m1_vehicle",
        input_data: {
          ...formData,
          chosen_price: userChosenPrice,
          suggested_price: suggestedPrice.toString(),
          is_using_custom_price: isUsingCustomPrice,
        },
        result_data: {
          ...resultToSave,
          safety_equipment: safetyEquipmentData,
          safety_score: calculateSafetyScore(),
          safety_level: calculateSafetyLevel(),
          safety_discount: calculateSafetyDiscount(),
          actual_cost_data: actualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);
      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => {
          navigate("/enduser/dashboard");
        }, 1500);
      } else {
        throw new Error(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("שגיאה בשמירה:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון ייבוא אישי - רכב M1",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  const formatCurrency = (amount: number, currency = "ILS") => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleGreenRatingGroupSelect = (groupNumber: number) => {
    handleInputChange("green_rating_group", groupNumber.toString());
    setShowGreenRatingModal(false);
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) {
      const errors = validateStep1();
      return errors.length === 0;
    }
    if (currentStep === 2) {
      return userChosenPrice && parseFloat(userChosenPrice) > 0;
    }
    return false;
  };

  const calculateSafetyScore = (): number => {
    let totalScore = 0;

    Object.entries(safetyEquipmentData.systems).forEach(([key, isEnabled]) => {
      if (isEnabled) {
        totalScore +=
          SAFETY_SYSTEMS_SCORES[key as keyof typeof SAFETY_SYSTEMS_SCORES];
      }
    });

    return totalScore;
  };

  const calculateSafetyLevel = (): number => {
    const score = calculateSafetyScore();
    const airbagsCount = safetyEquipmentData.airbagsCount;

    // בדיקת דרישות כריות אויר עבור M1 (6 מינימום)
    if (airbagsCount < 6) {
      return 0; // לא עומד בדרישות מינימום
    }

    // מיפוי לפי הניקוד החדש (2026):
    // רמה 0: פחות מ-1 נקודה
    // רמה 1: 1-1.5 נקודות
    // רמה 2: 2-2.5 נקודות
    // רמה 3: 3-3.5 נקודות
    // רמה 4: 4-4.5 נקודות
    // רמה 5: 5-5.5 נקודות
    // רמה 6: 6-6.5 נקודות
    // רמה 7: 7-7.5 נקודות
    // רמה 8: 8 נקודות ומעלה

    if (score >= 10) return 8;
    if (score >= 8) return 7;
    if (score >= 6) return 6;
    if (score >= 5) return 5;
    if (score >= 4) return 4;
    if (score >= 3) return 3;
    if (score >= 2) return 2;
    if (score >= 1) return 1;

    return 0;
  };

  const calculateSafetyDiscount = (): number => {
    const level = calculateSafetyLevel();
    const levelData = SAFETY_LEVELS.find((l) => l.level === level);
    return levelData?.discount || 0;
  };

  const handleAirbagsChange = (count: number) => {
    const validCount = isNaN(count) ? 0 : Math.max(0, Math.min(20, count));

    setSafetyEquipmentData((prev) => ({
      ...prev,
      airbagsCount: validCount,
    }));
  };

  const handleSafetySystemChange = (
    systemKey: keyof typeof SAFETY_SYSTEMS_SCORES,
    enabled: boolean,
  ) => {
    setSafetyEquipmentData((prev) => ({
      ...prev,
      systems: {
        ...prev.systems,
        [systemKey]: enabled,
      },
    }));
  };
  const formatGreenRatingDisplay = (groupNumber: string | number): string => {
    const num =
      typeof groupNumber === "string" ? parseInt(groupNumber) : groupNumber;

    if (num === 15) return "15א";
    if (num === 16) return "15ב";
    if (num === 17) return "15ג";

    return `קבוצה ${num}`;
  };

  const renderStep1 = () => (
    <MotionCard className="p-8">
      <h2 className="text-xl font-bold mb-6 text-center">פרטי הרכב והובלה</h2>
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-calculator"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setError(null);
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="terms-calculator"
            className="text-sm text-gray-700 flex-1"
          >
            קראתי ואני מאשר/ת את{" "}
            <Link
              to="/terms-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              תנאי השימוש ומדיניות הפרטיות
            </Link>{" "}
            *
          </label>
        </div>
      </div>
      <div className="space-y-6">
        <div>
          <label
            htmlFor="manufacturing_date"
            className="flex items-center mb-2 font-medium"
          >
            תאריך ייצור הרכב<span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.manufacturing_date}
              explanations={explanations}
            />
          </label>
          <input
            id="manufacturing_date"
            type="date"
            value={formData.manufacturing_date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange("manufacturing_date", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          {formData.manufacturing_date &&
            validateManufacturingDate(
              formData.manufacturing_date,
              formData.shipping_time_months,
            ) && (
              <p className="text-red-600 text-sm mt-1">
                {validateManufacturingDate(
                  formData.manufacturing_date,
                  formData.shipping_time_months,
                )}
              </p>
            )}
        </div>

        <div>
          <label
            htmlFor="vehicle_price_foreign"
            className="flex items-center mb-2 font-medium"
          >
            ערך הרכב במט"ח<span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.vehicle_price_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="vehicle_price_foreign"
            type="number"
            min="0"
            value={formData.vehicle_price_foreign}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNumberInputChange("vehicle_price_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="currency_type"
            className="flex items-center mb-2 font-medium"
          >
            סוג מט"ח<span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="currency_type"
            value={formData.currency_type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleInputChange("currency_type", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {currencyRates.map((rate) => (
              <option key={rate.currency_code} value={rate.currency_code}>
                {rate.currency_name_hebrew} ({rate.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין<span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="exchange_rate"
            type="number"
            min="0"
            step="0.0001"
            value={formData.exchange_rate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange("exchange_rate", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="shipping_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            ערך הובלה במט"ח <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.shipping_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="shipping_cost_foreign"
            type="number"
            value={formData.shipping_cost_foreign}
            min="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNumberInputChange("shipping_cost_foreign", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            ערך ביטוח במט"ח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_cost_foreign"
            type="number"
            min="0"
            value={formData.insurance_cost_foreign}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNumberInputChange("insurance_cost_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_currency_type"
            className="flex items-center mb-2 font-medium"
          >
            מטבע ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="insurance_currency_type"
            value={formData.insurance_currency_type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleInputChange("insurance_currency_type", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {currencyRates.map((rate) => (
              <option key={rate.currency_code} value={rate.currency_code}>
                {rate.currency_name_hebrew} ({rate.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="insurance_exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_exchange_rate"
            type="number"
            min="0"
            step="0.0001"
            value={formData.insurance_exchange_rate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange("insurance_exchange_rate", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="local_expenses_ils"
            className="flex items-center mb-2 font-medium"
          >
            הוצאות מקומיות בש"ח <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.local_expenses_ils}
              explanations={explanations}
            />
          </label>
          <input
            id="local_expenses_ils"
            type="number"
            min="0"
            value={formData.local_expenses_ils}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNumberInputChange("local_expenses_ils", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="border-t pt-6 mt-8">
          <h3 className="text-lg font-bold mb-4">📋 הצהרות ופרטים נוספים</h3>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  checked={formData.has_origin_declaration}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange(
                      "has_origin_declaration",
                      e.target.checked,
                    )
                  }
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="flex items-center font-medium">
                  יש לי הצהרת מקור בחשבון או תעודת מקור
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.has_origin_declaration}
                    explanations={explanations}
                  />
                </span>
              </label>
              {!formData.has_origin_declaration && (
                <p className="text-red-600 text-sm mt-2">
                  ⚠️ ללא הצהרת מקור יתווסף מס של 7%
                </p>
              )}
            </div>
            {/* TEMP: disabled by client request
            <div>
              <label className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  checked={formData.vehicle_weight_over_3500}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange(
                      "vehicle_weight_over_3500",
                      e.target.checked
                    )
                  }
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="flex items-center font-medium">
                  משקל הרכב מעל 3,500 ק"ג
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.vehicle_weight_over_3500}
                    explanations={explanations}
                  />
                </span>
              </label>
              {formData.vehicle_weight_over_3500 && (
                <p className="text-orange-600 text-sm mt-2">
                  🚛 רכבים כבדים: מס קנייה 72% (במקום 83%)
                </p>
              )}
            </div>
                */}
            {!formData.vehicle_weight_over_3500 && (
              <div>
                <label
                  htmlFor="green_rating_group"
                  className="flex items-center mb-2 font-medium"
                >
                  קבוצת ציון ירוק
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.green_rating_group}
                    explanations={explanations}
                  />
                  <CalculatorIcon
                    onClick={() => setShowGreenRatingModal(true)}
                    tooltip="למחשבון ציון ירוק"
                  />
                </label>
                <select
                  id="green_rating_group"
                  value={formData.green_rating_group}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleInputChange("green_rating_group", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {greenRatingGroups.length > 0
                    ? greenRatingGroups
                        .filter((group: any) => group.is_active)
                        .sort(
                          (a: any, b: any) => b.group_number - a.group_number,
                        )
                        .map((group: any) => (
                          <option
                            key={group.group_number}
                            value={group.group_number.toString()}
                          >
                            קבוצה {group.group_display_name}
                          </option>
                        ))
                    : // Fallback מתוקן למקרה שהנתונים לא נטענו
                      Array.from({ length: 17 }, (_, i: number) => {
                        const groupNum = 17 - i; // מ-17 ל-1 (בסדר יורד)
                        let displayName: string;

                        if (groupNum <= 14) {
                          displayName = groupNum.toString();
                        } else if (groupNum === 15) {
                          displayName = "15א";
                        } else if (groupNum === 16) {
                          displayName = "15ב";
                        } else if (groupNum === 17) {
                          displayName = "15ג";
                        } else {
                          // ברירת מחדל למקרים חריגים
                          displayName = groupNum.toString();
                        }

                        return (
                          <option key={groupNum} value={groupNum.toString()}>
                            קבוצה {displayName}
                          </option>
                        );
                      })}
                </select>
              </div>
            )}

            <div>
              <label
                htmlFor="road_entry_date"
                className="flex items-center mb-2 font-medium"
              >
                תאריך עליה לכביש<span className="text-red-500 mr-1">*</span>
                <span className="sr-only">(שדה חובה)</span>
                <ExplanationTooltip
                  stepKey={STEP_KEYS.road_entry_date}
                  explanations={explanations}
                />
              </label>
              <input
                id="road_entry_date"
                type="date"
                value={formData.road_entry_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange("road_entry_date", e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              {formData.road_entry_date &&
                formData.manufacturing_date &&
                validateRoadEntryDate(
                  formData.road_entry_date,
                  formData.manufacturing_date,
                ) && (
                  <p className="text-red-600 text-sm mt-1">
                    {validateRoadEntryDate(
                      formData.road_entry_date,
                      formData.manufacturing_date,
                    )}
                  </p>
                )}
            </div>
            <div>
              <label
                htmlFor="shipping_time_months"
                className="flex items-center mb-2 font-medium"
              >
                זמן הובלה צפוי
                <ExplanationTooltip
                  stepKey={STEP_KEYS.shipping_time_months}
                  explanations={explanations}
                />
              </label>
              <select
                id="shipping_time_months"
                value={formData.shipping_time_months}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleInputChange("shipping_time_months", e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="0">הרכב כבר בארץ</option>
                <option value="1">חודש עד הגעה לישראל</option>
                <option value="2">חודשיים עד הגעה לישראל</option>
                <option value="3">שלושה חודשים עד הגעה לישראל</option>
              </select>

              {/* תצוגת אזהרת מעבר שנה */}
              {(() => {
                const warning = checkYearTransitionWarning(
                  formData.manufacturing_date,
                  formData.shipping_time_months,
                );
                return warning ? (
                  <p className="text-orange-600 text-sm mt-2 bg-orange-50 p-2 rounded">
                    {warning}
                  </p>
                ) : null;
              })()}

              {/* עדכון תצוגת וולידציה של תאריך ייצור */}
              {formData.manufacturing_date &&
                validateManufacturingDate(
                  formData.manufacturing_date,
                  formData.shipping_time_months,
                ) && (
                  <p className="text-red-600 text-sm mt-1">
                    {validateManufacturingDate(
                      formData.manufacturing_date,
                      formData.shipping_time_months,
                    )}
                  </p>
                )}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="mt-4 p-3 bg-red-50 text-red-700">{error}</div>}

      <div className="flex justify-between mt-8">
        <MotionButton onClick={handleGoBack} variant="secondary">
          ← חזור
        </MotionButton>
        <MotionButton
          onClick={handleNextStep}
          disabled={!canProceedToNextStep() || isLoading}
        >
          {isLoading ? "מחשב..." : "חשב מחיר מומלץ →"}
        </MotionButton>
      </div>
    </MotionCard>
  );

  const renderStep2 = () => {
    if (!basicResult) return null;

    const hasLuxuryTax =
      basicResult.calculation_details?.luxury_tax_calculation;

    return (
      <MotionCard className="p-8">
        <h2 className="text-xl font-bold mb-6 text-center">
          💎 בחירת מחיר לצרכן
        </h2>

        <div className="text-center mb-6">
          <p className="text-lg mb-6">
            על סמך הנתונים שהזנת, חישבנו את המחיר לצרכן:
          </p>

          <div className="space-y-6">
            {/* שדה פשוט אחד כמו שהלקוח ביקש */}
            <div>
              <label
                htmlFor="target_consumer_price"
                className="flex items-center justify-center mb-2 font-medium"
              >
                מחיר לצרכן (בש"ח)
                <ExplanationTooltip
                  stepKey={STEP_KEYS.target_consumer_price}
                  explanations={explanations}
                />
              </label>
              <input
                id="target_consumer_price"
                type="text"
                inputMode="numeric"
                aria-required="true"
                value={formatThousands(userChosenPrice)}
                onChange={(e) =>
                  setUserChosenPrice(toRawNumber(e.target.value))
                }
                placeholder={`מחיר מומלץ: ${suggestedPrice.toLocaleString(
                  "he-IL",
                )}`}
                className="w-full max-w-md mx-auto p-4 text-xl text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="text-sm text-gray-500 mt-2">
                המחיר לצרכן : {formatCurrency(suggestedPrice)}
              </div>
              {hasLuxuryTax && (
                <div className="text-sm text-blue-600 mt-1">
                  הרכב חייב במס יוקרה - המחיר לצרכן מותאם לחסכון מקסימלי
                </div>
              )}
            </div>

            {hasLuxuryTax && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
                <h4 className="font-bold text-yellow-800 mb-2">
                  ℹ️ מידע על מס יוקרה
                </h4>
                <p className="text-sm text-yellow-700">
                  הרכב שלך עשוי להיות חייב במס יוקרה. בהתאם להוראות החוק המערכת
                  מחשבת את המחיר לצרכן לצורך חישוב מס יוקרה
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <MotionButton onClick={handleGoBack} variant="secondary">
            ← חזור לעריכת פרטים
          </MotionButton>

          <MotionButton
            onClick={handleNextStep}
            disabled={!canProceedToNextStep() || isLoading}
          >
            {isLoading ? "מחשב..." : "חשב תוצאות →"}
          </MotionButton>
        </div>
      </MotionCard>
    );
  };
  const renderStep3 = () => {
    if (!originalCalculationResult && !customCalculationResult) return null;

    // חיפוש מטבע לתצוגה
    const selectedCurrency = currencyRates.find(
      (c) => c.currency_code === formData.currency_type,
    );
    // const selectedInsuranceCurrency = currencyRates.find(
    //   (c) => c.currency_code === formData.insurance_currency_type
    // );

    return (
      <MotionCard className="p-8">
        <h3 className="text-xl font-bold mb-6 text-center">
          📊 תוצאות חישוב - רכב M1
        </h3>

        {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי", הערות וכפתורים) */}
        <div ref={printRef} className="space-y-6">
        <TaxYearNotice />

        {/* תצוגת אזהרת מעבר שנה בתוצאות (אם יש) */}
        {(() => {
          const warning = checkYearTransitionWarning(
            formData.manufacturing_date,
            formData.shipping_time_months,
          );
          return warning ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <h4 className="font-bold text-orange-800 mb-2">📅 הערת זמן</h4>
              <p className="text-sm text-orange-700">{warning}</p>
            </div>
          ) : null;
        })()}

        {/* נתוני הקלט שהוזנו - מהטופס המקורי */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <h4 className="font-bold text-lg mb-4 text-blue-800">
            נתוני הקלט שהוזנו:
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">תאריך ייצור:</span>
              <span className="font-medium">
                {new Date(formData.manufacturing_date).toLocaleDateString(
                  "he-IL",
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">ערך רכב:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.vehicle_price_foreign || "0",
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">מטבע:</span>
              <span className="font-medium">
                {selectedCurrency?.currency_name_hebrew} (
                {formData.currency_type})
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">שער חליפין:</span>
              <span className="font-medium">
                {parseFloat(formData.exchange_rate || "0").toFixed(4)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות הובלה:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.shipping_cost_foreign || "0",
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות ביטוח:</span>
              <span className="font-medium">
                {parseFloat(formData.insurance_cost_foreign || "0") > 0
                  ? `${parseFloat(
                      formData.insurance_cost_foreign || "0",
                    ).toLocaleString("he-IL")} ${
                      formData.insurance_currency_type
                    }`
                  : "ללא ביטוח"}
              </span>
            </div>

            {parseFloat(formData.insurance_cost_foreign || "0") > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">שער חליפין ביטוח:</span>
                <span className="font-medium">
                  {parseFloat(formData.insurance_exchange_rate || "0").toFixed(
                    4,
                  )}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">הוצאות מקומיות:</span>
              <span className="font-medium">
                {parseFloat(formData.local_expenses_ils || "0") > 0
                  ? `₪${parseFloat(
                      formData.local_expenses_ils || "0",
                    ).toLocaleString("he-IL")}`
                  : "אין הוצאות מקומיות"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">הצהרת מקור:</span>
              <span
                className={`font-medium ${
                  formData.has_origin_declaration
                    ? "text-green-600"
                    : "text-orange-600"
                }`}
              >
                {formData.has_origin_declaration
                  ? "יש הצהרה ✓"
                  : "אין הצהרה (+7%)"}
              </span>
            </div>
            {/* TEMP: disabled by client request
            <div className="flex justify-between">
              <span className="text-gray-600">משקל רכב:</span>
              <span className="font-medium">
                {formData.vehicle_weight_over_3500
                  ? "מעל 3.5 טון"
                  : "עד 3.5 טון ✓"}
              </span>
            </div>
              */}
            {!formData.vehicle_weight_over_3500 && (
              <div className="flex justify-between">
                <span className="text-gray-600">ציון ירוק:</span>
                <span className="font-medium" translate="no">
                  {formatGreenRatingDisplay(formData.green_rating_group)}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">תאריך עליה לכביש:</span>
              <span className="font-medium">
                {new Date(formData.road_entry_date).toLocaleDateString("he-IL")}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">זמן הובלה:</span>
              <span className="font-medium">
                {parseInt(formData.shipping_time_months) === 0
                  ? "הרכב כבר בארץ"
                  : `${formData.shipping_time_months} חודש${
                      parseInt(formData.shipping_time_months) > 1 ? "ים" : ""
                    }`}
              </span>
            </div>
          </div>
        </div>

        {/* אם המשתמש השתמש במחיר אחר - הצגת השוואה */}
        {isUsingCustomPrice &&
          originalCalculationResult &&
          customCalculationResult && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <h4 className="font-bold text-lg mb-4 text-yellow-800">
                חישוב לפי המחיר שבחרת
              </h4>

              <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">מחיר לצרכן שבחרת:</span>
                    <div className="text-lg font-bold text-green-700">
                      {formatCurrency(
                        customCalculationResult.chosen_result
                          .consumer_price_used,
                      )}
                    </div>
                  </div>

                  {/* <div>
                    <span className="font-medium">סה"כ עלות:</span>
                    <div className="text-xl font-bold text-green-800">
                      {formatCurrency(
                        customCalculationResult.chosen_result.total_cost
                      )}
                    </div>
                  </div> */}
                </div>

                {customCalculationResult.chosen_result
                  .luxury_tax_applicable && (
                  <div className="mt-3 text-sm">
                    <strong>מס יוקרה חל:</strong>{" "}
                    {customCalculationResult.chosen_result.luxury_tax_rate?.toFixed(
                      2,
                    )}
                    %
                    {customCalculationResult.chosen_result.months_on_road && (
                      <span className="mr-4">
                        <strong>חודשים על הכביש:</strong>{" "}
                        {customCalculationResult.chosen_result.months_on_road}
                      </span>
                    )}
                  </div>
                )}

                <div className="mt-3 text-sm text-orange-600 font-medium">
                  ⚠️ שים לב: המחיר שבחרת שונה מהמחיר שלנו
                </div>
              </div>

              {/* השוואה למחיר המומלץ */}
              <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                <h5 className="font-bold text-blue-800 mb-3">
                  להשוואה - המחיר שלנו:
                </h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">המחיר המומלץ:</span>
                    <div className="text-lg font-bold text-blue-700">
                      {formatCurrency(
                        originalCalculationResult.chosen_result
                          .consumer_price_used,
                      )}
                    </div>
                  </div>

                  <div>
                    <span className="font-medium">עלות עם המחיר המומלץ:</span>
                    <div className="text-xl font-bold text-blue-800">
                      {formatCurrency(
                        originalCalculationResult.chosen_result.total_cost,
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-gray-100 border border-gray-300 rounded text-sm">
                  <strong>הפרש בעלות:</strong>
                  {customCalculationResult.chosen_result.total_cost >
                  originalCalculationResult.chosen_result.total_cost ? (
                    <span className="text-red-600 font-bold mr-2">
                      +
                      {formatCurrency(
                        customCalculationResult.chosen_result.total_cost -
                          originalCalculationResult.chosen_result.total_cost,
                      )}{" "}
                      (יותר יקר)
                    </span>
                  ) : (
                    <span className="text-green-600 font-bold mr-2">
                      -
                      {formatCurrency(
                        originalCalculationResult.chosen_result.total_cost -
                          customCalculationResult.chosen_result.total_cost,
                      )}{" "}
                      (חסכון)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* תוצאה עיקרית - המחיר שהמשתמש בחר */}
        {!isUsingCustomPrice && originalCalculationResult && (
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-bold text-gray-800 mb-4">
              חישוב לפי המחיר שבחרת
            </h4>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">מחיר לצרכן:</span>
                <div className="text-lg font-bold text-gray-700">
                  {formatCurrency(
                    originalCalculationResult.chosen_result.consumer_price_used,
                  )}
                </div>
              </div>

              {/* <div>
                <span className="font-medium">סה"כ עלות:</span>
                <div className="text-2xl font-bold text-green-800">
                  {formatCurrency(
                    originalCalculationResult.chosen_result.total_cost
                  )}
                </div>
              </div> */}
            </div>

            {originalCalculationResult.chosen_result.luxury_tax_applicable && (
              <div className="mt-4">
                <div className="text-sm">
                  <strong>מס יוקרה חל:</strong>{" "}
                  {originalCalculationResult.chosen_result.luxury_tax_rate?.toFixed(
                    2,
                  )}
                  %
                  {originalCalculationResult.chosen_result.months_on_road && (
                    <span className="mr-4">
                      <strong>חודשים על הכביש:</strong>{" "}
                      {originalCalculationResult.chosen_result.months_on_road}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* פירוט החישוב */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h4 className="text-lg font-bold mb-4">פירוט החישוב:</h4>

          {(() => {
            // השתמש רק בתוצאה הנבחרת (הסר את "חישוב לפי מחיר מומלץ")
            const result = isUsingCustomPrice
              ? customCalculationResult
              : originalCalculationResult;
            if (!result) return null;

            const customsAmount = result.base_calculations.customs_fee;
            const customsRate = result.base_calculations.customs_rate_percent;
            const purchaseTax = result.chosen_result.final_purchase_tax;
            const purchaseTaxRate =
              result.chosen_result.final_purchase_tax_rate;
            const vatAmount = result.chosen_result.vat_amount;
            const taxValue = result.base_calculations.tax_value_ils;
            const totalTaxes = customsAmount + purchaseTax + vatAmount;
            const totalCost =
              result.chosen_result.total_cost ||
              taxValue + customsAmount + purchaseTax + vatAmount;

            // חישוב שיעור מס יוקרה אם יש
            const basePurchaseTaxRate = 83; // או 72 לרכבים כבדים
            const luxuryTaxRate = purchaseTaxRate - basePurchaseTaxRate;
            const hasLuxuryTax = luxuryTaxRate > 0;

            // חישוב שיעור המיסים הכללי
            const totalTaxesPercent = (totalTaxes / taxValue) * 100;

            return (
              <div className="space-y-3 text-black">
                {" "}
                {/* צבע שחור אחיד */}
                {/* 1. ערך לצרכי מכס */}
                <div className="flex justify-between">
                  <span className="text-black">ערך לצורכי מכס:</span>
                  <span className="font-medium text-black">
                    {formatCurrency(taxValue)}
                  </span>
                </div>
                {/* 2. סך המכס */}
                <div className="flex justify-between">
                  <span className="text-black">סך המכס:</span>
                  <span className="font-medium text-black">
                    {formatCurrency(customsAmount)}
                  </span>
                </div>
                {/* 3. שיעור המכס */}
                <div className="flex justify-between">
                  <span className="text-black">שיעור המכס:</span>
                  <span className="font-medium text-black">{customsRate}%</span>
                </div>
                {/* 4. סך מס הקניה */}
                <div className="flex justify-between">
                  <span className="text-black">סך מס הקניה:</span>
                  <span className="font-medium text-black">
                    {formatCurrency(purchaseTax)}
                  </span>
                </div>
                {/* 5. שיעור מס הקניה המלא */}
                <div className="flex justify-between">
                  <span className="text-black">שיעור מס הקניה המלא:</span>
                  <span className="font-medium text-black">
                    {purchaseTaxRate.toFixed(2)}%
                    {hasLuxuryTax && (
                      <span className="mr-2 text-sm text-black">
                        (כולל {luxuryTaxRate.toFixed(2)}% מס יוקרה)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black">שיעור מס קניה אפקטיבי:</span>
                  <span className="font-medium text-black" translate="no">
                    {result.chosen_result.effective_purchase_tax_rate?.toFixed(
                      2,
                    ) || "N/A"}
                    %
                  </span>
                </div>
                {/* הוספה: ציון שיעור מס יוקרה בנפרד אם יש */}
                {/* {hasLuxuryTax && (
                  <div className="flex justify-between">
                    <span className="text-black">שיעור מס יוקרה:</span>
                    <span className="font-medium text-black">
                      {luxuryTaxRate.toFixed(2)}%
                    </span>
                  </div>
                )} */}
                {/* 6. סך המע"מ */}
                <div className="flex justify-between">
                  <span className="text-black">סך המע"מ:</span>
                  <span className="font-medium text-black">
                    {formatCurrency(vatAmount)}
                  </span>
                </div>
                {/* 7. שיעור המעמ */}
                <div className="flex justify-between">
                  <span className="text-black">שיעור המע"מ:</span>
                  <span className="font-medium text-black">18%</span>
                </div>
                {/* 8. סך המיסים */}
                <div className="flex justify-between">
                  <span className="text-black">סך המיסים:</span>
                  <span className="font-medium text-black">
                    {formatCurrency(totalTaxes)}
                  </span>
                </div>
                {/* 9. שיעור המיסים */}
                <div className="flex justify-between">
                  <span className="text-black">שיעור המיסים:</span>
                  <span className="font-medium text-black">
                    {totalTaxesPercent.toFixed(2)}%
                  </span>
                </div>
                <hr className="my-3 border-gray-300" />
                {/* 10. עלות הרכב כולל מיסים */}
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-black">עלות הרכב כולל מיסים:</span>
                  <span className="text-black">
                    {formatCurrency(totalCost)}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* מחשבון אבזור בטיחותי */}
        <MotionCard className="p-6 border-2 border-blue-200 bg-blue-50">
          <h3 className="font-bold text-lg mb-4 text-blue-800">
            🛡️ מחשבון רמת אבזור בטיחותי (נלווה)
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            חישוב רמת אבזור בטיחותי על פי נוהל 03/13 משרד התחבורה - ייתכן ותהיה
            זכאי להחזר לאחר הטסט
            <br />
            <span className="font-semibold">
              שאל את עמיל המכס שלך לפרטים נוספים
            </span>
          </p>

          {/* מספר כריות אויר */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              כמה כריות אויר יש ברכב שלך? (נדרש מינימום 6 עבור M1)
            </label>
            <input
              type="number"
              min="0"
              max="20"
              value={
                safetyEquipmentData.airbagsCount === 0
                  ? ""
                  : safetyEquipmentData.airbagsCount
              }
              onChange={(e) =>
                handleAirbagsChange(parseInt(e.target.value) || 0)
              }
              placeholder="הכנס מספר כריות אויר"
              className="w-48 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="mr-2 text-sm text-gray-600">
              כריות (נדרש מינימום 6 עבור M1)
            </span>
            {safetyEquipmentData.airbagsCount < 6 && (
              <p className="text-red-600 text-xs mt-1">
                נדרש מינימום 6 כריות אויר עבור רכב M1
              </p>
            )}
          </div>

          {/* מערכות בטיחות */}
          <div className="space-y-3 mb-4">
            <h4 className="font-medium text-blue-800">מערכות בטיחות:</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.blindSpot}
                  onChange={(e) =>
                    handleSafetySystemChange("blindSpot", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת זיהוי כלי רכב ב"שטח מת" (1)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.sideCollisionActive}
                  onChange={(e) =>
                    handleSafetySystemChange(
                      "sideCollisionActive",
                      e.target.checked,
                    )
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת אקטיבית למניעת התנגשות צד ב"שטח מת" (2)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.reverseBraking}
                  onChange={(e) =>
                    handleSafetySystemChange("reverseBraking", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>בלימה אוטומטית בנסיעה לאחור (1)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.fatigueWarning}
                  onChange={(e) =>
                    handleSafetySystemChange("fatigueWarning", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת התרעה במצב של היסח הדעת הנהג (1)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.childAlert}
                  onChange={(e) =>
                    handleSafetySystemChange("childAlert", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת להתרעה על השארת ילדים ברכב (1.5)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.doorOpening}
                  onChange={(e) =>
                    handleSafetySystemChange("doorOpening", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת אחזרת פתיחת דלת (1.5)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.adaptiveLighting}
                  onChange={(e) =>
                    handleSafetySystemChange(
                      "adaptiveLighting",
                      e.target.checked,
                    )
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>מערכת תאורה אדפטיבית/שליטה באורות גבוהים (1)</span>
              </label>

              <label className="flex items-center space-x-2 rtl:space-x-reverse">
                <input
                  type="checkbox"
                  checked={safetyEquipmentData.systems.junctionBraking}
                  onChange={(e) =>
                    handleSafetySystemChange(
                      "junctionBraking",
                      e.target.checked,
                    )
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span>בלימת חירום להתנעה חוצה בצמתים (1.5)</span>
              </label>
            </div>
          </div>

          {/* תוצאות החישוב */}
          <div className="bg-white p-4 rounded-lg border border-blue-300">
            <h4 className="font-bold text-blue-800 mb-3">
              תוצאות חישוב אבזור בטיחותי:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">ניקוד כולל:</span>
                <div className="text-lg font-bold text-blue-600" translate="no">
                  {calculateSafetyScore().toFixed(1)}
                </div>
              </div>
              <div>
                <span className="font-medium">רמת אבזור:</span>
                <div className="text-lg font-bold text-blue-600" translate="no">
                  {calculateSafetyLevel()}
                </div>
              </div>
              <div>
                <span className="font-medium">סכום הנחה:</span>
                <div
                  className="text-lg font-bold text-green-600"
                  translate="no"
                >
                  {calculateSafetyDiscount().toLocaleString()} ש"ח
                </div>
              </div>
            </div>

            {safetyEquipmentData.airbagsCount < 6 &&
              safetyEquipmentData.airbagsCount > 0 && (
                <p className="text-orange-600 text-xs mt-1">
                  ⚠️ נדרש מינימום 6 כריות אויר עבור רכב M1 לקבלת רמת אבזור
                </p>
              )}
            {safetyEquipmentData.airbagsCount === 0 && (
              <p className="text-gray-500 text-xs mt-1">
                📝 אנא הכנס את מספר כריות האויר ברכב
              </p>
            )}

            <div className="mt-3 text-xs text-blue-600">
              * ייתכן ותהיה זכאי לאחר הטסט להחזר בגין אבזור בטיחותי (שאל את עמיל
              המכס שלך)
              <br />* חישוב נלווה בלבד - לא משפיע על עלות הייבוא הסופית
            </div>
          </div>
        </MotionCard>

        {/* דייק לי את התוצאה */}
        <ActualCostCalculator
          vehicleCurrency={formData.currency_type}
          vehicleExchangeRate={formData.exchange_rate}
          vehiclePriceForeign={formData.vehicle_price_foreign}
          shippingCostForeign={formData.shipping_cost_foreign}
          insuranceCurrency={formData.insurance_currency_type}
          insuranceExchangeRate={formData.insurance_exchange_rate}
          insuranceCostForeign={formData.insurance_cost_foreign}
          localExpensesIls={formData.local_expenses_ils}
          totalTaxes={(() => {
            const result = isUsingCustomPrice
              ? customCalculationResult
              : originalCalculationResult;
            if (!result) return 0;
            const customs = result.base_calculations?.customs_fee || 0;
            const purchaseTax = result.chosen_result?.final_purchase_tax || 0;
            const vat = result.chosen_result?.vat_amount || 0;
            return customs + purchaseTax + vat - calculateSafetyDiscount();
          })()}
          onDataChange={setActualCostData}
          explanations={explanations}
          initialData={actualCostData}
        />
        </div>
        {/* סוף אזור התוצאה הניתן להדפסה */}

        {/* שדה הערות ופעולות */}
        <div className="space-y-4 print-exclude">
          <div>
            <label className="block mb-2 font-medium">
              הערות (אופציונלי - עד 1000 תווים):
            </label>
            <textarea
              value={userNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (e.target.value.length <= 1000) {
                  setUserNotes(e.target.value);
                }
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="הוסף הערות או הסברים נוספים לחישוב זה..."
            />
            <div className="text-sm text-gray-500 mt-1">
              {userNotes.length}/1000 תווים
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {isSaved && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              ✅ החישוב נשמר בהצלחה! מעביר לדף הבית...
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-8">
          <MotionButton
            onClick={handleGoBack}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            ← חזור לעריכה
          </MotionButton>

          <MotionButton
            onClick={handlePrint}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200"
          >
            🖨️ הדפס
          </MotionButton>

          <MotionButton
            onClick={handleSaveCalculation}
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            {isSaving ? "שומר..." : isSaved ? "נשמר ✓" : "שמור וחזור לדף הבית"}
          </MotionButton>
        </div>
      </MotionCard>
    );
  };

  const renderProgressBar = () => {
    const totalSteps = 3;
    const progress = (currentStep / totalSteps) * 100;

    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            שלב {currentStep} מתוך {totalSteps}
          </span>
          <span className="text-sm font-medium text-blue-600">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const renderStepLabels = () => {
    const steps = [
      { number: 1, label: "פרטי רכב", icon: "1" },
      { number: 2, label: "בחירת מחיר", icon: "2" },
      { number: 3, label: "תוצאות", icon: "3" },
    ];

    return (
      <div className="flex justify-between mb-8">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`flex flex-col items-center ${
              currentStep >= step.number ? "text-blue-600" : "text-gray-600"
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                currentStep >= step.number
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {currentStep > step.number ? "✓" : step.icon}
            </div>
            <span className="text-xs font-medium text-center">
              {step.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading && !currencyRates.length) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען נתוני מחשבון...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-4 md:pt-6" dir="rtl">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון ייבוא אישי - רכב M1
          </h1>
          <p className="text-gray-600">
            חישוב מס לרכב פרטי M1 מונע במנוע בנזין, דיזל, היברידי, פלאג אין
          </p>
          <p className="text-blue-600"> במשקל כולל עד 3.5 טון</p>
        </div>

        {renderProgressBar()}
        {renderStepLabels()}

        {error && (
          <MotionCard className="mb-6 p-4 bg-red-50 border border-red-200">
            <div className="flex items-center">
              <span className="text-red-600 text-lg ml-2">❌</span>
              <span className="text-red-800">{error}</span>
            </div>
          </MotionCard>
        )}

        <div className="mb-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>

      <GreenRatingCalculatorModal
        isOpen={showGreenRatingModal}
        onClose={() => setShowGreenRatingModal(false)}
        onSelectGroup={handleGreenRatingGroupSelect}
        currentGroupNumber={parseInt(formData.green_rating_group) || undefined}
      />
    </div>
  );
};

export default M1VehicleCalculatorPage;
